import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Shield, Fingerprint, FileCode, ArrowRight } from 'lucide-react';
import { SEO } from '@/components/seo';
import { buttonStyles } from '@/components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface Watermark {
  id: string;
  slug: string;
  name: string;
  developer: string;
  description: string;
  type: string;
  technology: string;
  isRemovable: boolean;
  logoUrl: string | null;
}

export function WatermarksHub() {
  const [watermarks, setWatermarks] = useState<Watermark[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/pages/watermarks`)
      .then(res => res.json())
      .then(data => setWatermarks(data.watermarks || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'invisible': return <Fingerprint className="w-5 h-5" />;
      case 'metadata': return <FileCode className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'invisible': return 'bg-purple-100 text-purple-600';
      case 'metadata': return 'bg-blue-100 text-blue-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <>
      <SEO
        title="AI Watermark Types - SynthID, C2PA, Stable Signature & More"
        description="Learn about different AI watermark types used by image generators. Understand how SynthID, C2PA, and other watermarks work and how to remove them."
        canonical="/watermarks"
      />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-purple-50 via-white to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              AI Watermark Types
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Learn about different watermarking technologies used by AI image generators
              and how to remove them from your images.
            </p>
            <Link to="/" className={buttonStyles({ size: 'lg' })}>
              Remove Watermarks Free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Watermarks Grid */}
      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {watermarks.map(watermark => (
                <Link
                  key={watermark.id}
                  to={`/watermarks/${watermark.slug}`}
                  className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    {watermark.logoUrl ? (
                      <img
                        src={watermark.logoUrl}
                        alt={`${watermark.name} logo`}
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(watermark.type)}`}>
                        {getTypeIcon(watermark.type)}
                      </div>
                    )}
                    {watermark.isRemovable && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        Removable
                      </span>
                    )}
                  </div>

                  <h2 className="text-xl font-semibold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                    {watermark.name}
                  </h2>
                  <p className="text-sm text-gray-500 mb-3">by {watermark.developer}</p>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {watermark.description}
                  </p>

                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {watermark.type}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {watermark.technology}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Remove Any AI Watermark
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Our tool removes invisible watermarks, metadata, and AI signatures from any image.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Try Free Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
}

export default WatermarksHub;
